/* ------------------------------------------------------------------
   /frontend/src/pages/dashboard/post/AccountSelector.js  (v1.6 – UI Refresh)
   • Same logic as v1.6, modernized UI only.
   • Professional SaaS aesthetic — card layout, selected ring, hover states.
------------------------------------------------------------------- */
import React, { useContext } from 'react';
import { AccountsContext }   from '../../../context/AccountsContext';
import { getPlatformIcon, getPlatformColor } from '../accounts/helpers/platformIcons';

export default function AccountSelector({ selectedAccounts, setSelectedAccounts, inferDefaultFormat }) {
  const { accounts: allAccounts, loading } = useContext(AccountsContext);

  const getPrimaryId = acc =>
    acc.id || acc.twitterId || acc.instagram_user_id || acc.fb_page_id || acc.igId ||
    acc.platformId || acc.externalId || acc.xId || acc.lnId || acc.threadsId ||
    acc.tiktokId || acc.pinterestId || acc.youtubeId || acc.channelId;

  function handleToggleAccount(acc) {
    const primaryId = getPrimaryId(acc);
    if (!primaryId) return;

    setSelectedAccounts(prev => {
      const found = prev.find(p => p.id === primaryId && p.platform === acc.platform);
      if (found) return prev.filter(p => !(p.id === primaryId && p.platform === acc.platform));

      return [...prev, {
        id: primaryId,
        externalId:
            acc.platform === 'x'         && acc.twitterId         ? acc.twitterId
          : acc.platform === 'twitter'   && acc.twitterId         ? acc.twitterId
          : acc.platform === 'instagram' && acc.instagram_user_id ? acc.instagram_user_id
          : acc.platform === 'facebook'  && acc.fb_page_id        ? acc.fb_page_id
          : acc.platform === 'linkedin'  && acc.lnId              ? acc.lnId
          : acc.platform === 'threads'   && acc.threadsId         ? acc.threadsId
          : acc.platform === 'tiktok'    && acc.tiktokId          ? acc.tiktokId
          : acc.platform === 'pinterest' && acc.pinterestId       ? acc.pinterestId
          : acc.platform === 'youtube'   && acc.youtubeId         ? acc.youtubeId
          : acc.externalId || primaryId,
        platform   : acc.platform,
        displayName: acc.displayName || acc.username || '',
        userAvatar : acc.profileImage || acc.igProfilePic || acc.fb_page_picture_url || acc.channelThumbnail || '',
        platformIcon: getPlatformIcon(acc.platform),
        options    : { format: inferDefaultFormat(acc.platform) },
      }];
    });
  }

  /* ─────────────────────────── UI ─────────────────────────── */
  return (
    <div style={{
      background: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: 12,
      padding: '18px 20px',
      marginBottom: 16,
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#6b7280' }}>
          Post To
        </span>
        {selectedAccounts.length > 0 && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 9px', borderRadius: 20,
            background: '#eff6ff', color: '#2563eb', border: '1px solid #dbeafe',
          }}>
            {selectedAccounts.length} selected
          </span>
        )}
      </div>

      {/* Account grid */}
      {loading && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: '#9ca3af', fontSize: 13 }}>
          <div className="spinner-border spinner-border-sm" style={{ color: '#d1d5db' }} />
          Loading accounts…
        </div>
      )}

      {!loading && allAccounts.length === 0 && (
        <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>No connected accounts found.</p>
      )}

      {!loading && allAccounts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {allAccounts.map(acc => {
            const primaryId  = getPrimaryId(acc);
            const isSelected = selectedAccounts.some(s => s.id === primaryId && s.platform === acc.platform);
            const avatar     = acc.profileImage || acc.igProfilePic || '/img/default-avatar.png';
            const name       = acc.displayName || acc.username || acc.platform;

            return (
              <button
                key={`${acc.platform}-${primaryId}`}
                type="button"
                onClick={() => handleToggleAccount(acc)}
                title={name}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 5,
                  padding: '8px 6px 6px',
                  borderRadius: 10,
                  border: `2px solid ${isSelected ? '#2563eb' : '#e5e7eb'}`,
                  background: isSelected ? '#eff6ff' : '#fafafa',
                  cursor: 'pointer',
                  width: 68,
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  outline: 'none',
                }}
              >
                {/* Selected checkmark */}
                {isSelected && (
                  <div style={{
                    position: 'absolute', top: -6, right: -6,
                    width: 18, height: 18, borderRadius: '50%',
                    background: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid #fff',
                  }}>
                    <i className="fas fa-check" style={{ fontSize: 8, color: '#fff' }} />
                  </div>
                )}

                {/* Avatar with platform badge */}
                <div style={{ position: 'relative', width: 40, height: 40, flexShrink: 0 }}>
                  <img
                    src={avatar}
                    alt={name}
                    style={{
                      width: 40, height: 40, borderRadius: '50%', objectFit: 'cover',
                      border: `2px solid ${isSelected ? '#93c5fd' : '#e5e7eb'}`,
                      transition: 'border-color 0.15s ease',
                    }}
                  />
                  {/* Platform icon badge */}
                  <div style={{
                    position: 'absolute', bottom: -2, right: -2,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', border: '1.5px solid #e5e7eb',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <i
                      className={getPlatformIcon(acc.platform)}
                      style={{ fontSize: 8, color: getPlatformColor(acc.platform) }}
                    />
                  </div>
                </div>

                {/* Account name */}
                <span style={{
                  fontSize: 10, fontWeight: 500, color: isSelected ? '#1d4ed8' : '#6b7280',
                  maxWidth: 56, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  lineHeight: 1.2, textAlign: 'center',
                }}>
                  {name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}